import { CommonModule } from '@angular/common';
import { Component, computed, inject, Input, output, signal } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../services/auth.service';
import { InputFieldComponent } from '../../../../shared/components/ui/input-field/input-field.component';
import { CtaButtonComponent } from '../../../../shared/components/ui/cta-button/cta-button.component';
import { ParentButtonComponent } from '../../../../shared/components/ui/parent-button/parent-button.component';
import { getTakeUntilDestroyed } from '../../../../core/helpers/take-until-destroy.helper';
import { AppToasterService } from '../../../../core/services/toaster.service';

@Component({
  selector: 'app-forgot-password-request',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslatePipe,
    InputFieldComponent,
    CtaButtonComponent,
    ParentButtonComponent,
  ],
  templateUrl: './forgot-password-request.component.html',
  styleUrl: './forgot-password-request.component.scss',
})
export class ForgotPasswordRequestComponent {
  #authService = inject(AuthService);
  #router = inject(Router);
  #toaster = inject(AppToasterService);
  #fb = inject(FormBuilder);
  #translate = inject(TranslateService);

  private takeUntilDestroyed = getTakeUntilDestroyed();
  #embedded = signal(false);
  #layout = signal<string | undefined>(undefined);

  readonly isLoading = computed(() =>
    this.#authService.resetPasswordRequest.isPending(),
  );
  readonly form = this.#fb.nonNullable.group({
    userName: ['', [Validators.required, Validators.pattern(/^[A-Za-z0-9._]+$/)]],
  });

  readonly userNameValidation = signal<Record<string, string>>({
    required: '',
    userName: '',
  });
  readonly embedded = computed(() => this.#embedded());
  readonly isParentLayout = computed(
    () => this.#layout() === 'parent' || this.#layout() === 'admin',
  );

  requested = output<string>();
  back = output<void>();

  @Input() set isEmbedded(value: boolean) {
    this.#embedded.set(value);
  }

  @Input() set layout(value: string | undefined) {
    this.#layout.set(value);
  }

  ngOnInit(): void {
    this.#translate
      .get(['app.auth.requiredUserName', 'app.auth.validUserName'])
      .pipe(this.takeUntilDestroyed())
      .subscribe((translations) => {
        this.userNameValidation.set({
          required: translations['app.auth.requiredUserName'],
          userName: translations['app.auth.validUserName'],
        });
      });
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const userName = this.form.getRawValue().userName;
    this.#authService.resetPasswordRequest
      .mutateAsync({ userName })
      .then(() => {
        this.#toaster.success(
          this.#translate.instant('app.auth.resetCodeSentMessage'),
        );
        if (this.embedded()) {
          this.requested.emit(userName);
          return;
        }
        this.#router.navigate(['/auth/update-password'], {
          queryParams: { userName },
        });
      });
  }

  goToSignin(): void {
    if (this.embedded()) {
      this.back.emit();
      return;
    }
    this.#router.navigate(['/auth']);
  }
}
